import {Injectable} from '@angular/core';
import { createAuth0Client, Auth0Client } from '@auth0/auth0-spa-js';
import {BehaviorSubject, combineLatest, from, Observable, of, throwError} from 'rxjs';
import {catchError, concatMap, shareReplay, switchMap, tap} from 'rxjs/operators';
import {Router} from '@angular/router';
import {environment} from '../../environments/environment';
import {fromFetch} from 'rxjs/fetch';
import {HttpService} from './http.service';
import {map} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  // Create an observable of Auth0 instance of client
  auth0Client$ = (from(
    createAuth0Client({
      domain: environment.auth0Domain,
      clientId: environment.auth0ClientId,
      authorizationParams: {
        redirect_uri: `${window.location.origin}`
      },
      cacheLocation: 'localstorage'
    })
  ) as Observable<Auth0Client>).pipe(
    shareReplay(1), // Every subscription receives the same shared value
    catchError(err => throwError(err))
  );
  // Define observables for SDK methods that return promises by default
  // For each Auth0 SDK method, first ensure the client instance is ready
  // concatMap: Using the client instance, call SDK method; SDK returns a promise
  handleRedirectCallback$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.handleRedirectCallback()))
  );
  // Create a local property for login status
  loggedIn: boolean = null;
  // from: Convert that resulting promise into an observable
  isAuthenticated$ = this.auth0Client$.pipe(
    concatMap((client: Auth0Client) => from(client.isAuthenticated())),
    tap(res => this.loggedIn = res)
  );
  // Create subject and public observable of user profile data
  private userProfileSubject$ = new BehaviorSubject<any>(null);
  userProfile$ = this.userProfileSubject$.asObservable();

  constructor(private router: Router, private httpService: HttpService) {
    // On initial load, check authentication state with authorization server
    // Set up local auth streams if user is already authenticated
    this.localAuthSetup();
    // Handle redirect from Auth0 login
    this.handleAuthCallback();
  }

  // When calling, options can be passed if desired
  // https://auth0.github.io/auth0-spa-js/classes/auth0client.html#getuser
  getUser$(options?): Observable<any> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) => from(client.getUser())),
      tap(user => this.userProfileSubject$.next(user))
    );
  }

  // https://auth0.github.io/auth0-spa-js/classes/auth0client.html#gettokensilently
  getTokenSilently$(options?): Observable<string> {
    return this.auth0Client$.pipe(
      concatMap((client: Auth0Client) =>
        from(client.getTokenSilently(options)).pipe(
          map((res: any) => (typeof res === 'string' ? res : res && res.access_token))
        )
      )
    );
  }


  // When calling, options can be passed if desired

  login(redirectPath: string = '/') {
    // A desired redirect path can be passed to login method
    // (e.g., from a route guard)
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log in
      client.loginWithRedirect({
        authorizationParams: {
          redirect_uri: `${window.location.origin}`
        },
        appState: {target: redirectPath}
      });
    });
  }

  logout() {
    // Ensure Auth0 client instance exists
    this.auth0Client$.subscribe((client: Auth0Client) => {
      // Call method to log out
      client.logout({
        logoutParams: {
          returnTo: window.location.origin
        }
      });
      localStorage.removeItem('userRoles');
      localStorage.removeItem('currentCustomer');
    });
  }

  private getUserProfile(accessToken): Observable<any> {
    return fromFetch('https://dev-ut9hcoi3.auth0.com/userinfo', {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    }).pipe(
      switchMap(response => {
        if (response.ok) {
          // OK return data
          return response.json();
        } else {
          // Server is returning a status requiring the client to try something else.
          return of({error: true, message: `Error ${response.status}`});
        }
      }),
      catchError(err => {
        // Network or other error, handle appropriately
        console.error(err);
        return of({error: true, message: err.message});
      })
    );
  }

  private localAuthSetup() {
    // This should only be called on app initialization
    // Set up local authentication streams
    const checkAuth$ = this.isAuthenticated$.pipe(
      concatMap((loggedIn: boolean) => {
        if (loggedIn) {
          // If authenticated, get user and set in app
          // NOTE: you could pass options here if needed
          return this.getUser$();
        }
        // If not authenticated, return stream that emits 'false'
        return of(loggedIn);
      })
    );
    checkAuth$.subscribe();
  }

  private handleAuthCallback() {
    // Call when app reloads after user logs in with Auth0
    const params = window.location.search;
    if (params.includes('code=') && params.includes('state=')) {
      let targetRoute: string; // Path to redirect to after login processsed
      const authComplete$ = this.handleRedirectCallback$.pipe(
        // Have client, now call method to handle auth callback redirect
        tap(cbRes => {
          // Get and set target redirect route from callback results
          targetRoute = cbRes.appState && cbRes.appState.target ? cbRes.appState.target : '/';
        }),
        concatMap(() => {
          // Redirect callback complete; get user and login status
          return combineLatest([
            this.getUser$(),
            this.isAuthenticated$
          ]);
        })
      );
      // Subscribe to authentication completion observable
      // Response will be an array of user and login status
      authComplete$.subscribe(([user, loggedIn]) => {
        // Redirect to target route after callback processing
        this.saveRoles();
      });
    } else {
      if (!(localStorage.getItem('currentCustomer') && localStorage.getItem('userRoles'))) {
        this.saveRoles();
      }
    }
  }

  private saveRoles() {
    this.getTokenSilently$().subscribe(accessToken => {
      this.getUserProfile(accessToken).subscribe(profile => {
        // const roles = profile[environment.rolesKey];
        // console.log('roles: ' + roles);
        // localStorage.setItem('userRoles', roles);
        this.httpService.getUserRoleListByAuth0Id(profile.sub)
          .subscribe(roles => {
            if (roles && roles.length > 0) {
              console.log('getUserRoleListByAuth0Id roles = ' + roles.join(','));
              localStorage.setItem('userRoles', roles.join(','));
              this.httpService.getCustomerByAuth0Id(profile.sub)
                .subscribe(customer => {
                  if (customer) {
                    localStorage.setItem('currentCustomer', JSON.stringify(customer));
                    window.location.reload();
                  }
                }, error => {
                  console.log('getCustomerByAuth0Id error = ' + error);
                  this.logout();
                }, () => {
                  console.log('getCustomerByAuth0Id complete');
                });
            } else {
              console.log('empty role list');
              localStorage.setItem('userRoles', '[]');
              // this.logout();
            }
          }, error => {
            console.log('getUserRoleListByAuth0Id error = ' + error);
            this.logout();
          }, () => {
            console.log('getUserRoleListByAuth0Id complete');
          });
      });
    });
  }

}
